import { Form } from "@amzn/aquila-feedback-web-assets";
import { TextButton } from "@amzn/storm-ui";
import type { FC } from "react";
import React, { useEffect, useState } from "react";
import { useStore } from "zustand";

import { useGetReportIssueForm } from "../../hooks/aquila/reportIssue/useGetReportIssueForm/useGetReportIssueForm";
import { useSubmitReportIssueForm } from "../../hooks/aquila/reportIssue/useSubmitReportIssueForm/useSubmitReportIssueForm";
import { authStore } from "../../store/auth";
import { navigationStore } from "../../store/navigation";
import { reportIssueStore } from "../../store/reportIssue";
import { AquilaReportIssueTriggerType } from "../../types/aquila";
import { isNotExecuted, isSuccess } from "../../utils/api.utils";
import { isAllInsightsTab } from "../../utils/tab.utils";

const InternalReportIssue: FC = () => {
    const [aquilaConfig] = useStore(authStore, (state) => [
        state.settings.aquilaConfig,
    ]);
    const [activeTab] = useStore(navigationStore, (state) => [state.activeTab]);
    const [activeModal, closeModal] = useStore(reportIssueStore, (state) => [
        state.activeModal,
        state.closeModal,
    ]);
    const [
        formConfig,
        getReportIssueFormConfigAsyncStatus,
        getFormConfigAsyncAction,
    ] = useGetReportIssueForm({
        reportIssueFormConfig: aquilaConfig.sidePanelReportIssueConfig,
    });
    const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
    const [, submitFormAsyncAction] = useSubmitReportIssueForm({
        reportIssueFormConfig: aquilaConfig.sidePanelReportIssueConfig,
        trigger: isAllInsightsTab(activeTab)
            ? AquilaReportIssueTriggerType.ALL_INSIGHTS_SIDEPANEL
            : AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_SIDEPANEL,
    });

    useEffect(() => {
        if (activeModal === AquilaReportIssueTriggerType.SIDE_PANEL) {
            if (isSuccess(getReportIssueFormConfigAsyncStatus) && formConfig) {
                setIsFeedbackFormOpen(true);
            } else {
                if (
                    isNotExecuted(getReportIssueFormConfigAsyncStatus) &&
                    aquilaConfig.sidePanelReportIssueConfig?.configId
                ) {
                    void getFormConfigAsyncAction.execute();
                }
            }
        }
    }, [activeModal, getReportIssueFormConfigAsyncStatus, formConfig]);

    const handleOnSubmit = (formData) => {
        void submitFormAsyncAction.execute(formData);
        setIsFeedbackFormOpen(false);
        closeModal();
    };

    const handleOnCancel = () => {
        setIsFeedbackFormOpen(false);
        closeModal();
    };

    useEffect(() => {
        if (
            aquilaConfig.sidePanelReportIssueConfig?.configId &&
            isNotExecuted(getReportIssueFormConfigAsyncStatus)
        ) {
            void getFormConfigAsyncAction.execute();
        }
    }, []);

    return isSuccess(getReportIssueFormConfigAsyncStatus) && formConfig ? (
        <>
            <TextButton onClick={() => setIsFeedbackFormOpen(true)}>
                Report an Issue
            </TextButton>
            {isFeedbackFormOpen && (
                <Form
                    {...formConfig}
                    configId={aquilaConfig.sidePanelReportIssueConfig.configId}
                    onSubmit={handleOnSubmit}
                    onCancel={handleOnCancel}
                    isOpen={isFeedbackFormOpen}
                />
            )}
        </>
    ) : (
        <></>
    );
};

export const SidePanelReportIssue = React.memo(InternalReportIssue);
